import { Outlet, redirect } from '@tanstack/react-router';
import { FeatureFlagGuard } from '@/components/guards';
import { AccountFeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import { useLocation, useParams } from '@/services/router/compat';
import { designStudioDashboardUrl, designStudioEditorIndexTarget } from '@/services/router/design-studio-href';
import { lazyRoute } from '@/services/router/prefetch';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { DesignStudioRoute, LastWorkspaceResolver, LazyPage } from './-route-layouts';

// Design Studio's editor pages, behind the `ds-editor-in-hydra` kill-switch. Flag on: `route-ownership.ts`
// claims these URLs for hydra and these routes render them. Flag off: ownership falls back to the
// embedded owner and each route renders the Design Studio bundle host instead, which routes the URL
// itself, exactly as before. Ownership and what renders flip together on the one flag.
//
// Flag on, the routes render the editor's pages, inside its shell and layout (kept mounted while
// moving between pages).

const P = '/workspaces/:workspaceId/design-studio';
export const DesignStudioEmailEditorRoute = lazyRoute(
  `${P}/editor/:id`,
  () => import('@/pages/workspaces/design-studio/editor/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);
export const DesignStudioWhatsappEditorRoute = lazyRoute(
  `${P}/editor/whatsapp/:id`,
  () => import('@/pages/workspaces/design-studio/editors/whatsapp/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);
export const DesignStudioInboxMessageEditorRoute = lazyRoute(
  `${P}/editor/inbox-message/:id`,
  () => import('@/pages/workspaces/design-studio/editors/inbox-message/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);
export const DesignStudioSmsEditorRoute = lazyRoute(
  `${P}/editor/sms/:id`,
  () => import('@/pages/workspaces/design-studio/editors/sms/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);
export const DesignStudioSignUpFormEditorRoute = lazyRoute(
  `${P}/editor/sign-up-form/:id`,
  () => import('@/pages/workspaces/design-studio/editors/sign-up-form/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);
export const DesignStudioInAppEditorRoute = lazyRoute(
  `${P}/editor/in-app/:id`,
  () => import('@/pages/workspaces/design-studio/editors/in-app/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);
export const DesignStudioInboxPreviewsRoute = lazyRoute(
  `${P}/editor/:id/inbox-previews/:runId`,
  () => import('@/pages/workspaces/design-studio/inbox-previews/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);
export const DesignStudioInboxPreviewRoute = lazyRoute(
  `${P}/editor/:id/inbox-previews/:runId/previews/:previewId`,
  () => import('@/pages/workspaces/design-studio/inbox-previews/preview/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);
export const DesignStudioJourneysTemplateRoute = lazyRoute(
  `${P}/journeys/:templateWorkspaceId/:templateId`,
  () => import('@/pages/workspaces/design-studio/journeys/[workspaceId]/[templateId]/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);
export const DesignStudioBrandRoute = lazyRoute(
  `${P}/brand`,
  () => import('@/pages/workspaces/design-studio/brand/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);
export const DesignStudioAssetsRoute = lazyRoute(
  `${P}/assets`,
  () => import('@/pages/workspaces/design-studio/assets/index.page'),
  { flag: AccountFeatureFlag.DsEditorInHydra },
);

// Lazy so the editor's shell, stores and providers stay out of the entry chunk.
const EditorLayout = lazyRoute(`${P}/editor`, () => import('@/features/design-studio/editor/editor-layout'), {
  flag: AccountFeatureFlag.DsEditorInHydra,
});

/** The layout around the workspace's Design Studio editor pages. */
export function DesignStudioEditorLayoutRoute() {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.DsEditorInHydra} fallback={DesignStudioRoute}>
      <LastWorkspaceResolver>
        <LazyPage>
          <EditorLayout>
            <LazyPage>
              <Outlet />
            </LazyPage>
          </EditorLayout>
        </LazyPage>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

type WorkspaceRouteContext = { params: { workspaceId: string } };

// The redirects run twice on purpose. `beforeLoad` handles in-app navigations, where the account is
// already loaded. On a cold page load `beforeLoad` runs before the account (so the flag reads off and
// it lets the route through); the matching component then redirects once the layout has the flag.

/** `beforeLoad` for `/design-studio/welcome`, which only leads to the dashboard. */
export function redirectToDesignStudioDashboard({ params }: WorkspaceRouteContext) {
  if (!hasFeatureFlag(AccountFeatureFlag.DsEditorInHydra)) {
    return;
  }

  throw redirect({ href: designStudioDashboardUrl(params.workspaceId), replace: true });
}

/** `beforeLoad` for `/design-studio/editor`: `?file=<id>` opens that file; without it, the dashboard. */
export function redirectFromEditorIndex({
  params,
  location,
}: WorkspaceRouteContext & { location: { searchStr: string } }) {
  if (!hasFeatureFlag(AccountFeatureFlag.DsEditorInHydra)) {
    return;
  }

  throw redirect({ href: designStudioEditorIndexTarget(params.workspaceId, location.searchStr), replace: true });
}

/** The component behind `redirectToDesignStudioDashboard`, for a cold page load. */
export function DesignStudioDashboardRedirectRoute() {
  const { workspaceId = 'last' } = useParams<{ workspaceId?: string }>();
  return <SafeNavigate to={designStudioDashboardUrl(workspaceId)} options={{ shouldReplaceHistoryEntry: true }} />;
}

/** The component behind `redirectFromEditorIndex`, for a cold page load. */
export function DesignStudioEditorIndexRedirectRoute() {
  const { workspaceId = 'last' } = useParams<{ workspaceId?: string }>();
  const { search } = useLocation();
  return (
    <SafeNavigate
      to={designStudioEditorIndexTarget(workspaceId, search)}
      options={{ shouldReplaceHistoryEntry: true }}
    />
  );
}
